import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SubscriptionContext = createContext(null);

/**
 * Subscription state shape (matches backend /api/subscription/status):
 *   { state: "trial"|"active"|"expired"|"exempt", expires_at, days_remaining,
 *     can_use, amount_paise, currency, is_trial, razorpay_key_id, razorpay_configured }
 */
export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);

  // Manual refresh — used by Subscribe page after a successful payment.
  const refresh = useCallback(async () => {
    if (!userId) return null;
    setLoading(true);
    try {
      const { data } = await api.get("/subscription/status");
      setSub(data);
      return data;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial fetch + 5-min polling, keyed on userId so logout/login cleanly re-fetches.
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    api.get("/subscription/status").then((r) => { if (!cancelled) setSub(r.data); });
    const t = setInterval(() => {
      api.get("/subscription/status").then((r) => { if (!cancelled) setSub(r.data); });
    }, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [userId]);

  // Effective sub: clear when no logged-in user, so a previous user's state doesn't leak.
  const effectiveSub = userId ? sub : null;

  const value = useMemo(() => ({
    sub: effectiveSub,
    loading,
    refresh,
    canUse: effectiveSub?.can_use !== false,
    isExpired: effectiveSub?.state === "expired",
    isTrial: effectiveSub?.state === "trial",
    isActive: effectiveSub?.state === "active",
    isExempt: effectiveSub?.state === "exempt",
    daysRemaining: effectiveSub?.days_remaining ?? null,
    amountPaise: effectiveSub?.amount_paise ?? 10000,
    currency: effectiveSub?.currency ?? "INR",
    razorpayKeyId: effectiveSub?.razorpay_key_id || "",
    razorpayConfigured: !!effectiveSub?.razorpay_configured,
  }), [effectiveSub, loading, refresh]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export const useSubscription = () => useContext(SubscriptionContext) || {
  sub: null, canUse: true, isExpired: false, isTrial: false, isActive: false, isExempt: false,
  daysRemaining: null, refresh: () => Promise.resolve(null), razorpayConfigured: false,
};
